import { useEffect, useState, useCallback } from "react";
import { adminApi, getApiErrorMessage } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UserPlus, ShieldPlus } from "lucide-react";

function CreateForm({ title, icon: Icon, fields, onSubmit, testPrefix }) {
  const empty = Object.fromEntries(fields.map((f) => [f.key, ""]));
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(form);
      setForm(empty);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Icon className="h-4 w-4 text-[var(--cta)]" /> {title}</CardTitle></CardHeader>
      <CardContent>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={submit}>
          {fields.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label>{f.label}</Label>
              <Input type={f.type || "text"} required value={form[f.key]}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                placeholder={f.placeholder} data-testid={`${testPrefix}-${f.key}-input`} />
            </div>
          ))}
          <div className="flex items-end sm:col-span-2">
            <Button type="submit" disabled={loading} className="rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid={`${testPrefix}-submit-button`}>
              {loading ? "Creating..." : title}
            </Button>
          </div>
        </form>
        <p className="mt-3 text-xs text-[var(--ink-muted)]">
          Temp password must be 8+ chars with upper, lower and a digit. The account holder will be forced to create their own password on first login.
        </p>
      </CardContent>
    </Card>
  );
}

export default function UserManagement() {
  const { admin } = useAdminAuth();
  const isSuper = admin?.role === "super_admin";
  const [admins, setAdmins] = useState([]);

  const loadAdmins = useCallback(() => {
    if (!isSuper) return;
    adminApi.get("/admin/users/admins").then((r) => setAdmins(r.data)).catch(() => {});
  }, [isSuper]);

  useEffect(() => { loadAdmins(); }, [loadAdmins]);

  const createClient = async (form) => {
    await adminApi.post("/admin/users/clients", {
      full_name: form.full_name, email: form.email, phone: form.phone, temp_password: form.temp_password,
    });
    toast.success(`Client account created for ${form.email}`);
  };

  const createAdmin = async (form) => {
    await adminApi.post("/admin/users/admins", {
      full_name: form.full_name, email: form.email, temp_password: form.temp_password,
    });
    toast.success(`Admin account created for ${form.email}`);
    loadAdmins();
  };

  return (
    <AdminLayout>
      <h1 className="font-heading text-3xl text-[var(--brand)]" data-testid="user-management-title">User Accounts</h1>
      <p className="mt-1 text-sm text-[var(--ink-muted)]">Create client{isSuper ? " and admin" : ""} accounts. New accounts must set their own password on first login.</p>

      <div className="mt-6 space-y-6">
        <CreateForm
          title="Create Client Account" icon={UserPlus} testPrefix="create-client"
          onSubmit={createClient}
          fields={[
            { key: "full_name", label: "Full name", placeholder: "Client's full name" },
            { key: "email", label: "Email", type: "email", placeholder: "client@example.com" },
            { key: "phone", label: "Phone", placeholder: "10-digit mobile" },
            { key: "temp_password", label: "Temp password", placeholder: "e.g. Welcome2026" },
          ]}
        />

        {isSuper && (
          <>
            <CreateForm
              title="Create Admin Account" icon={ShieldPlus} testPrefix="create-admin"
              onSubmit={createAdmin}
              fields={[
                { key: "full_name", label: "Full name", placeholder: "Admin's full name" },
                { key: "email", label: "Email", type: "email", placeholder: "staff@smifs.com" },
                { key: "temp_password", label: "Temp password", placeholder: "e.g. Welcome2026" },
              ]}
            />

            <Card>
              <CardHeader><CardTitle className="text-base">Admin Accounts</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="admins-table">
                    <thead>
                      <tr className="border-b border-[var(--glass-border)] text-left text-xs text-[var(--ink-muted)]">
                        <th className="py-2 pr-4">Name</th>
                        <th className="py-2 pr-4">Email</th>
                        <th className="py-2 pr-4">Role</th>
                        <th className="py-2 pr-4">Status</th>
                        <th className="py-2">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {admins.map((a) => (
                        <tr key={a.id} className="border-b border-[var(--glass-border)]/60" data-testid={`admin-row-${a.email}`}>
                          <td className="py-2.5 pr-4 font-medium">{a.full_name}</td>
                          <td className="py-2.5 pr-4">{a.email}</td>
                          <td className="py-2.5 pr-4">
                            <Badge variant={a.role === "super_admin" ? "default" : "secondary"} className={a.role === "super_admin" ? "bg-[var(--brand)]" : ""}>{a.role}</Badge>
                          </td>
                          <td className="py-2.5 pr-4 text-xs">
                            {a.must_set_password ? <span className="text-amber-600">Awaiting first login</span> : <span className="text-emerald-700">Active</span>}
                          </td>
                          <td className="py-2.5 text-xs text-[var(--ink-muted)]">{(a.created_at || "").slice(0, 10)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
